import { midi } from './synth.js';

/**
 * Named sound effects built from the synth primitives.
 * Everything here is 8-bit flavoured: square leads, triangle thumps, LFSR noise.
 */

export function createSfx(synth) {
  const { tone, noise, sequence } = synth;

  return {
    /** Ball bounces off Vaus. Pitch shifts with where it struck. */
    paddle(offset = 0) {
      const base = 300 + offset * 90;
      tone({ type: 'square', freq: base, freqEnd: base * 0.72, duration: 0.07, gain: 0.42 });
    },

    wall() {
      tone({ type: 'square', freq: 232, freqEnd: 190, duration: 0.045, gain: 0.3 });
    },

    /** Higher rows ring brighter, like the arcade board did. */
    brick(row = 0, rows = 8) {
      const ratio = 1 - row / Math.max(1, rows);
      const freq = 320 + ratio * 520;
      tone({ type: 'square', freq, freqEnd: freq * 0.82, duration: 0.055, gain: 0.34 });
    },

    brickBreak(row = 0, rows = 8) {
      this.brick(row, rows);
      noise({ duration: 0.09, gain: 0.22, filterFreq: 2600, filterEnd: 700, q: 0.9 });
    },

    /** Silver brick took a hit but survived. */
    brickTough() {
      tone({ type: 'triangle', freq: 190, freqEnd: 140, duration: 0.09, gain: 0.4 });
      noise({ duration: 0.06, gain: 0.16, filterFreq: 1400, q: 2.4 });
    },

    /** Gold brick shrugged it off. */
    brickBlocked() {
      tone({ type: 'square', freq: 148, freqEnd: 132, duration: 0.05, gain: 0.26 });
      noise({ duration: 0.05, gain: 0.14, filterFreq: 900, q: 3 });
    },

    launch() {
      sequence([midi(69), midi(76)], { type: 'square', step: 0.05, gain: 0.34 });
    },

    capsule() {
      sequence([midi(72), midi(76), midi(79), midi(84)], {
        type: 'square',
        step: 0.055,
        gain: 0.36,
      });
    },

    capsuleBad() {
      sequence([midi(64), midi(60), midi(55)], { type: 'square', step: 0.07, gain: 0.34 });
    },

    laser() {
      tone({ type: 'sawtooth', freq: 1180, freqEnd: 240, duration: 0.11, gain: 0.24 });
      noise({ duration: 0.06, gain: 0.12, filterFreq: 3400, filterEnd: 900 });
    },

    extraLife() {
      sequence([midi(72), midi(77), midi(81), midi(84), midi(89)], {
        type: 'square',
        step: 0.07,
        gain: 0.34,
      });
    },

    lifeLost() {
      sequence(
        [
          { freq: midi(64), duration: 0.16 },
          { freq: midi(59), duration: 0.16 },
          { freq: midi(55), duration: 0.16 },
          { freq: midi(48), duration: 0.42 },
        ],
        { type: 'triangle', step: 0.16, gain: 0.42 },
      );
      noise({ duration: 0.5, gain: 0.18, filterFreq: 1200, filterEnd: 140, delay: 0.02 });
    },

    roundStart() {
      sequence([midi(67), midi(72), midi(76)], { type: 'square', step: 0.1, gain: 0.36 });
    },

    roundClear() {
      sequence(
        [midi(72), midi(76), midi(79), midi(84), midi(79), midi(84), midi(88)],
        { type: 'square', step: 0.095, gain: 0.36 },
      );
    },

    warp() {
      tone({ type: 'square', freq: 180, freqEnd: 1700, duration: 0.55, gain: 0.3 });
      tone({ type: 'triangle', freq: 90, freqEnd: 850, duration: 0.55, gain: 0.24, delay: 0.03 });
      noise({ duration: 0.6, gain: 0.16, filterFreq: 400, filterEnd: 4800, q: 1.6 });
    },

    gameOver() {
      sequence(
        [
          { freq: midi(60), duration: 0.28 },
          { freq: midi(56), duration: 0.28 },
          { freq: midi(53), duration: 0.28 },
          { freq: midi(48), duration: 0.75 },
        ],
        { type: 'triangle', step: 0.3, gain: 0.44 },
      );
    },

    dohAppear() {
      tone({ type: 'sawtooth', freq: 120, freqEnd: 44, duration: 1.1, gain: 0.3 });
      noise({ duration: 1.2, gain: 0.18, filterFreq: 300, filterEnd: 90, q: 1.2 });
    },

    dohHit() {
      tone({ type: 'square', freq: 210, freqEnd: 96, duration: 0.13, gain: 0.42 });
      noise({ duration: 0.14, gain: 0.24, filterFreq: 1600, filterEnd: 320, q: 1.1 });
    },

    dohShoot() {
      tone({ type: 'sawtooth', freq: 460, freqEnd: 150, duration: 0.16, gain: 0.24 });
    },

    dohDefeat() {
      for (let i = 0; i < 7; i += 1) {
        noise({
          duration: 0.38,
          gain: 0.3,
          filterFreq: 2400 - i * 260,
          filterEnd: 90,
          q: 0.8,
          delay: i * 0.19,
        });
        tone({
          type: 'square',
          freq: 240 - i * 22,
          freqEnd: 50,
          duration: 0.3,
          gain: 0.26,
          delay: i * 0.19,
        });
      }
    },

    victory() {
      sequence(
        [
          midi(72), midi(76), midi(79), midi(84),
          midi(83), midi(79), midi(84), midi(88), midi(91),
        ],
        { type: 'square', step: 0.13, gain: 0.36 },
      );
      sequence([midi(48), midi(55), midi(52), midi(60)], {
        type: 'triangle',
        step: 0.29,
        gain: 0.3,
      });
    },

    uiMove() {
      tone({ type: 'square', freq: 620, freqEnd: 720, duration: 0.035, gain: 0.22 });
    },

    uiSelect() {
      sequence([midi(76), midi(83)], { type: 'square', step: 0.05, gain: 0.3 });
    },

    pause(on) {
      sequence(on ? [midi(74), midi(69)] : [midi(69), midi(74)], {
        type: 'square',
        step: 0.07,
        gain: 0.28,
      });
    },
  };
}
